import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AddressElement, PaymentElement } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useTranslation } from '@frontend/i18n';
import { usePrintReceipt } from '@frontend/payments-hooks';
import { PaymentsFlowType, StripeElementsWrapper } from '@frontend/payments-stripe-wrapper';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  ContentLoader,
  EmailField,
  Text,
  useControlledField,
  useFormField,
} from '@frontend/design-system';
import { useInvoicePerson } from '../../../../hooks/use-invoice-person';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useUpdatePaidRequestPoll } from '../../../hooks/use-update-paid-request-poll';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { ManualCardEntryNavigationSwitch } from './manual-card-entry-navigation-switch';
import { useStripeManualCardEntryPayment } from './manual-card-entry-stripe/use-stripe-manual-card-entry-payment';
import { ManualCardEntryStepBodyProps } from './manual-card-entry.types';

const DATA_TRACKING_PREFIX = `pay-client-virtualterminal`;

const styles = {
  emailField: css`
    margin-bottom: ${theme.spacing(1.5)};
  `,
  printReceiptField: css`
    margin-top: ${theme.spacing(1.5)};
    margin-bottom: ${theme.spacing(0.5)};
  `,
  sectionTitle: css`
    margin: ${theme.spacing(2, 0)};
  `,
};

export const ManualCardEntryStripe = () => {
  const { invoice } = useCollectPaymentMultiContext();
  return (
    <StripeElementsWrapper type={PaymentsFlowType.ManualCardEntry} locationId={invoice?.locationId}>
      <ManualCardEntryNavigationSwitch body={ManualCardEntryStripeBody} />
    </StripeElementsWrapper>
  );
};

const ManualCardEntryStripeBody = ({ onGoBack, onCompleteStep, onPaymentSuccess }: ManualCardEntryStepBodyProps) => {
  const { t } = useTranslation('payments');

  const {
    invoice,
    invoiceQuery: { refetch: refetchInvoice },
    activeGateway,
  } = useCollectPaymentMultiContext();

  const { personEmail, personFullName, personAddress } = useInvoicePerson(invoice);

  const [emailReceipt, setEmailReceipt] = useState<string | undefined>(personEmail);
  const [cardReadyToSubmit, setCardReadyToSubmit] = useState<boolean>(false);
  const [addressComplete, setAddressComplete] = useState<boolean>(false);
  const [cardName, setCardName] = useState<string | undefined>(undefined);
  const { value: receiptEmail, ...receiptEmailProps } = useControlledField({
    required: false,
    value: emailReceipt,
    type: 'email',
    onChange: (value) => {
      setEmailReceipt(value);
    },
  });

  useEffect(() => {
    setEmailReceipt(personEmail);
  }, [personEmail]);

  const printReceiptField = useFormField({
    type: 'checkbox',
    value: false,
  });
  const shouldPrintReceipt = !!printReceiptField.value;
  const printReceipt = usePrintReceipt();

  const handleCardChange = (event: StripePaymentElementChangeEvent) => setCardReadyToSubmit(!!event.complete);

  const handleAddressChange = (event: StripeAddressElementChangeEvent) => {
    setAddressComplete(!!event.complete);
    if (event.complete) setCardName(event.value.name);
  };

  const validate = () => !!cardName && !receiptEmailProps.error && cardReadyToSubmit && addressComplete;

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoice().then((res) => res.data);
  }, [refetchInvoice]);

  const { makeManualCardEntryPayment, makingManualCardEntryPayment } = useStripeManualCardEntryPayment({
    cardName,
    receiptEmail,
    invoice,
    onInvoiceAlreadyPaid: onGoBack,
    onRefetchInvoice,
  });

  const { setProcessingAndStartPoll } = useUpdatePaidRequestPoll(invoice, (updatedInvoice) => {
    onPaymentSuccess();
    if (shouldPrintReceipt) {
      printReceipt(updatedInvoice);
    }
  });

  const onSubmit = async () => {
    if (await makeManualCardEntryPayment()) {
      setProcessingAndStartPoll();

      onCompleteStep();
      return true;
    }

    return false;
  };

  const { backButton: backButtonTrackingId, primaryButton: primaryButtonTrackingId } = useScopedTrackingIds({
    backButton: `manual-card-entry--${activeGateway}--back-btn`,
    primaryButton: `manual-card-entry--${activeGateway}--primary-btn`,
  });

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.manualCardEntry}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.manualCardEntry]}
      isValid={validate() && !makingManualCardEntryPayment}
      footer={{
        primary: {
          children: shouldPrintReceipt ? t('Complete Payment & Print Receipt') : t('Complete Payment'),
          disabled: !validate(),
          trackingId: primaryButtonTrackingId,
        },
        secondary: {
          children: t('Back'),
          disabled: makingManualCardEntryPayment,
          trackingId: backButtonTrackingId,
        },
        showBackButton: false,
        onClickPrimary: async () => !!(await onSubmit()),
        onClickSecondary: onGoBack,
      }}
    >
      <ContentLoader show={makingManualCardEntryPayment} message={t('Making Manual Card Entry Payment...')} />

      <form>
        <PaymentElement onChange={handleCardChange} />
        <Text size='large' weight='bold' as='h3' css={styles.sectionTitle}>
          {t('Billing Address')}
        </Text>
        <AddressElement
          onChange={handleAddressChange}
          options={{
            mode: 'billing',
            defaultValues: {
              name: personFullName,
              address: personAddress,
            },
          }}
        />
        <Text size='large' weight='bold' as='h3' css={styles.sectionTitle}>
          {t('Email Receipt (Optional)')}
        </Text>
        <EmailField
          {...receiptEmailProps}
          actionText={'Clear'}
          css={styles.emailField}
          data-trackingid={`${DATA_TRACKING_PREFIX}-txt-emailreceipt`}
          hasNonFloatingLabel={true}
          label={t('Email')}
          name='email'
          onActionClick={emailReceipt ? () => setEmailReceipt('') : undefined}
          value={receiptEmail}
        />
        <CheckboxField
          {...printReceiptField}
          css={styles.printReceiptField}
          data-trackingid={`${DATA_TRACKING_PREFIX}-chk-printreceipt`}
          label={t('Print Receipt')}
          name='print-receipt'
        />
      </form>
    </StyledStep>
  );
};
